import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import * as Sentry from '@sentry/angular';

import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { ErrorInterceptor } from './interceptor/error.interceptor';
import { SpinnerInterceptor } from './interceptor/spinner/spinner.interceptor';
import { ApiService } from './services/api.service';
import { AuthenticationService } from './services/authentication.service';
import { EncryptionService } from './services/encryption.service';
import { GoogleMapsLoaderService } from './services/google-maps-loader.service';
import { SentryService } from '../sentry.config';
import { SentryErrorInterceptor } from './interceptor/sentry-error.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/constants/', '.json');
}

export function initializeGoogleMapsLoader(googleMapsLoader: GoogleMapsLoaderService) {
  return () => googleMapsLoader.load();
}

// Custom Sentry Error Handler
export class CustomSentryErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    console.error('Global error caught:', error);

    // Add additional context to the error
    Sentry.withScope((scope) => {
      scope.setTag('error_boundary', 'global');
      scope.setLevel('error');
      scope.setContext('error_details', {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });

      if (error?.rejection) {
        // Handle promise rejections
        scope.setTag('error_type', 'unhandled_promise_rejection');
        Sentry.captureException(error.rejection);
      } else {
        scope.setTag('error_type', 'global_error');
        Sentry.captureException(error);
      }
    });

    // Also log to console in development
    if (!environment.production) {
      console.error('Detailed error:', error);
    }
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    ClipboardModule,
    AppRoutingModule,
    ToastrModule.forRoot({
      maxOpened: 1,
      autoDismiss: true,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      newestOnTop: true,
    }),
    InlineSVGModule.forRoot(),
    PaginationModule.forRoot(),
    NgxSpinnerModule,
    TooltipModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgbModule,
    SharedModule,
  ],
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    AuthenticationService,
    ApiService,
    EncryptionService,
    SentryService,

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SentryErrorInterceptor, // New Sentry-specific interceptor
      multi: true,
    },

    // Google Maps
    {
      provide: Loader,
      useFactory: () =>
        new Loader({
          apiKey: environment.googleMaps.apiKey,
        }),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGoogleMapsLoader,
      deps: [GoogleMapsLoaderService],
      multi: true,
    },

    // Sentry Configuration
    {
      provide: ErrorHandler,
      useClass: CustomSentryErrorHandler, // Custom error handler with more context
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    Sentry.TraceService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor() {
    // Set up global unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      SentryService.captureException(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
        type: 'unhandled_promise_rejection',
        reason: event.reason,
        promise: event.promise,
      });
    });
  }
}
